<template>
  <PageChrome :show-header="false">
    <div class="contactDetail" v-if="contact">
      <!-- Header -->
      <header class="ctHeader">
        <div class="ctIdentity">
          <span class="ctLogo">
            <FavIcon :domain="accountDomain" :size="36" />
          </span>
          <div class="ctText">
            <h1 class="ctName">{{ primaryName }}</h1>
            <div class="ctSubline" v-if="jobTitle">{{ jobTitle }}</div>
            <div class="ctMeta">
              <RouterLink :to="accountHref" class="ctCompany">{{ accountName }}</RouterLink>
              <span v-if="contactEmail" class="ctDetail">
                <VerificationMark data-type="email" :status="contact?.provenance?.email?.leadiq_status" />
                {{ contactEmail }}
                <DataProvenance v-if="contact?.provenance?.email"
                  :provider="contact.provenance.email.provider"
                  data-type="email"
                  :data-value="contactEmail"
                />
              </span>
              <span v-if="mobilePhone" class="ctDetail">
                <VerificationMark data-type="phone" :status="contact?.provenance?.phone?.leadiq_status" />
                {{ mobilePhone }}
                <DataProvenance v-if="contact?.provenance?.phone"
                  :provider="contact.provenance.phone.provider"
                  data-type="phone"
                  :data-value="mobilePhoneRaw || mobilePhone"
                />
              </span>
              <span v-if="contactLocation" class="ctDetail">{{ contactLocation }}</span>
            </div>
            <div v-if="contactLinks.length" class="ctLinks">
              <a
                v-for="link in contactLinks"
                :key="link.label"
                :href="link.href"
                class="ctExtLink"
                target="_blank"
                rel="noopener"
                :title="link.label"
              >
                <FavIcon :domain="link.domain" :size="14" />
                <span>{{ link.label }}</span>
              </a>
            </div>
          </div>
        </div>

        <div class="ctHeaderRight">
          <span class="crmSync">
            <span v-if="crmRefreshing" class="crmSpinner" />
            <span v-else-if="crmSyncLabel" class="crmSyncLabel">{{ crmSyncLabel }}</span>
          </span>
          <span v-if="lifecycleState" class="statePill" :class="lifecyclePillClass">
            {{ humanize(lifecycleState) }}
          </span>
        </div>
      </header>

      <!-- Sales play context -->
      <section v-if="memberships.length" class="spSection">
        <h2 class="sectionTitle">Sales Play Enrollment</h2>
        <div class="spCards">
          <div v-for="m in memberships" :key="m.membership_id" class="spCard">
            <div class="spCardHeader">
              <RouterLink :to="`/sales-plays/${m.sales_play_id}`" class="spName">{{ m.sales_play_name }}</RouterLink>
              <span class="statePill" :class="memberStatePillClass(m.state)">{{ humanize(m.state) }}</span>
            </div>
            <div v-if="m.outcome_reason" class="spStatus">{{ humanize(m.outcome_reason) }}<template v-if="m.outcome_detail"> — {{ m.outcome_detail }}</template></div>

            <div v-for="a in m.actions" :key="a.action_id" class="spSeq">
              <div class="spSeqHeader">
                <span class="spSeqName">{{ humanize(a.action_type) }}</span>
                <span class="statePill mini" :class="actionStatusPillClass(a)">{{ actionStatusLabel(a) }}</span>
              </div>
              <div v-if="a.enrollment" class="spSeqProgress">
                <div class="progressBar">
                  <div class="progressFill" :style="{ width: seqProgress(a.enrollment) + '%' }"></div>
                </div>
                <span class="progressLabel">Step {{ a.enrollment.current_step_index + 1 }} / {{ a.enrollment.total_steps }}</span>
              </div>
              <div v-if="a.run_status === 'needs_attention'" class="spSeqReason">
                {{ a.reason }}
                <button type="button" class="resolveBtn" @click="openResolveDialog(m, a)">Resolve</button>
              </div>
              <div v-else-if="a.comment" class="spSeqComment">{{ a.comment }}</div>
            </div>
          </div>
        </div>
      </section>

      <NeedsAttentionResolveDialog
        v-if="resolving"
        :play-id="resolving.playId"
        :action-id="resolving.actionId"
        :action-run-id="resolving.actionRunId"
        :membership-id="resolving.membershipId"
        :reason="resolving.reason"
        :contact-label="resolving.contactLabel"
        :play-name="resolving.playName"
        :action-type="resolving.actionType"
        @close="resolving = null"
        @resolved="onResolved"
      />

      <!-- Timeline -->
      <section class="timelineSection">
        <h2 class="sectionTitle">Activity Timeline</h2>
        <ActivityTimeline :account-id="accountId" :contact-id="routeContactId" />
      </section>
    </div>
    <div v-else-if="loadError" class="muted loadMsg">Contact not found.</div>
    <div v-else class="muted loadMsg">Loading...</div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import DataProvenance from '../../components/DataProvenance.vue'
import VerificationMark from '../../components/VerificationMark.vue'
import { formatPhone } from '../utils/phone'
import FavIcon from '../../components/FavIcon.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { getSalesforceUrl } from '../../services/salesforce'
import { getHubspotUrl, getPipedriveUrl, preloadCrmLinkConfig } from '../../services/crmLinks'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_CONTACT } from '../../drawer/drawerTypes'
import { useAccountBasics } from '../../hooks/useAccountBasics'
import ActivityTimeline from '../components/ActivityTimeline.vue'
import NeedsAttentionResolveDialog from '../components/NeedsAttentionResolveDialog.vue'

type GenericMap = Record<string, unknown>

type ProvenanceEntry = {
  provider?: string | null
  leadiq_status?: string | null
}

type AccountContactResponse = {
  id: string
  email: string
  mobile_phone?: string | null
  provenance?: Record<string, ProvenanceEntry> | null
  display?: GenericMap
  salesforce?: GenericMap | null
  buying_team?: GenericMap | null
  city?: string | null
  state?: string | null
  country_code?: string | null
  timezone?: string | null
}


type SeqEnrollment = {
  enrollment_id: string
  sequence_id: string
  sequence_name: string
  status: string
  exit_reason: string | null
  current_step_index: number
  total_steps: number
  enrolled_at: string | null
}

type MembershipAction = {
  action_id: string
  action_type: string
  run_status: string | null
  reason: string | null
  comment: string | null
  action_run_id: string | null
  enrollment: SeqEnrollment | null
}

type PlayMembership = {
  membership_id: string
  sales_play_id: string
  sales_play_name: string
  state: string
  work_phase: string | null
  outcome_reason: string | null
  outcome_detail: string | null
  tagged_at: string | null
  last_agent_run_at: string | null
  actions: MembershipAction[]
}

type LifecycleCardData = {
  lifecycle_state: string
  sales_play_memberships: PlayMembership[]
}


const route = useRoute()
const { setContext, openDrawer } = useContextDrawer()

const contact = ref<AccountContactResponse | null>(null)
const accountId = ref('')
const { basics: accountBasics } = useAccountBasics(accountId)
const lifecycle = ref<LifecycleCardData | null>(null)
const loadError = ref(false)

const crmReady = ref(false)
const routeContactId = computed(() => String(route.params.id || '').trim())

const display = computed<GenericMap>(() => contact.value?.display && typeof contact.value.display === 'object' ? contact.value.display : {})
const salesforce = computed<GenericMap>(() => contact.value?.salesforce && typeof contact.value.salesforce === 'object' ? contact.value.salesforce : {})
const buyingTeam = computed<GenericMap>(() => contact.value?.buying_team && typeof contact.value.buying_team === 'object' ? contact.value.buying_team : {})

const primaryName = computed(() =>
  [display.value.name, display.value.full_name, contact.value?.email].map(v => String(v || '').trim()).find(Boolean) || 'Contact'
)
const jobTitle = computed(() =>
  [display.value.title, display.value.job_title, display.value.headline, buyingTeam.value.role].map(v => String(v || '').trim()).find(Boolean) || ''
)
const contactEmail = computed(() => String(contact.value?.email || '').trim())
// The stored mobile is the key provenance is filed under; the display/CRM chain is only a fallback.
const mobilePhoneRaw = computed(() => String(contact.value?.mobile_phone || '').trim())
const mobilePhone = computed(() =>
  mobilePhoneRaw.value
    ? formatPhone(mobilePhoneRaw.value)
    : [display.value.mobile_phone, display.value.mobile, display.value.phone, salesforce.value.MobilePhone, salesforce.value.Phone].map(v => String(v || '').trim()).find(Boolean) || ''
)
const contactLocation = computed(() => {
  const city = String(contact.value?.city || '').trim()
  const state = String(contact.value?.state || '').trim()
  const cc = String(contact.value?.country_code || '').trim()
  const tz = String(contact.value?.timezone || '').trim()
  const parts = [city, state, cc].filter(Boolean)
  const location = parts.join(', ')
  if (!location) return ''
  return tz ? `${location} (${tz})` : location
})

const accountDomain = computed(() => accountBasics.value?.domain || '')
const accountName = computed(() => accountBasics.value?.name || accountBasics.value?.domain || '')
const accountHref = computed(() => accountId.value ? `/accounts/${encodeURIComponent(accountId.value)}` : '#')

const contactLinks = computed(() => {
  void crmReady.value
  const links: Array<{ label: string; href: string; domain: string }> = []
  const linkedinUrl = [display.value.linkedin_url, display.value.linkedin, salesforce.value.LinkedIn_URL__c, salesforce.value.LinkedInUrl]
    .map(v => String(v || '').trim()).find(Boolean)
  const sfContactId = String(salesforce.value.contact_id || '').trim()
  const sfUrl = sfContactId ? getSalesforceUrl({ Id: sfContactId }, 'Contact') : null
  if (linkedinUrl) links.push({ label: 'LinkedIn', href: linkedinUrl, domain: 'linkedin.com' })
  if (sfUrl) links.push({ label: 'Salesforce', href: sfUrl, domain: 'salesforce.com' })
  const hsId = String(display.value.hubspot_contact_id || '').trim()
  const hsUrl = hsId ? getHubspotUrl('contact', hsId) : null
  if (hsUrl) links.push({ label: 'HubSpot', href: hsUrl, domain: 'hubspot.com' })
  const pdId = String(display.value.pipedrive_person_id || '').trim()
  const pdUrl = pdId ? getPipedriveUrl('person', pdId) : null
  if (pdUrl) links.push({ label: 'Pipedrive', href: pdUrl, domain: 'pipedrive.com' })
  return links
})

const lifecycleState = computed(() => lifecycle.value?.lifecycle_state || '')
const memberships = computed(() => lifecycle.value?.sales_play_memberships || [])

const lifecyclePillClass = computed(() => {
  const s = lifecycleState.value
  if (s === 'customer') return 'pillSuccess'
  if (s === 'engaged' || s === 'replied') return 'pillSuccess'
  if (s === 'outbound') return 'pillInfo'
  if (s === 'qualified') return 'pillInfo'
  if (s === 'staged') return 'pillWarning'
  if (s === 'nurture') return 'pillWarning'
  if (s === 'disqualified' || s === 'unsubscribed') return 'pillDanger'
  return 'pillMuted'
})

function memberStatePillClass(state: string): string {
  if (state === 'qualified' || state === 'active') return 'pillInfo'
  if (state === 'engaged' || state === 'completed') return 'pillSuccess'
  if (state === 'staged') return 'pillWarning'
  if (state === 'dismissed' || state === 'disqualified' || state === 'timed_out') return 'pillDanger'
  return 'pillMuted'
}

/** The action's own run status — never derived from the membership's
 * state, which only reflects the qualification/action phase, not what any
 * one action is actually doing. */
function actionStatusLabel(a: MembershipAction): string {
  if (a.run_status === null || a.run_status === 'pending') return 'Not yet started'
  if (a.run_status === 'dispatched' || a.run_status === 'in_progress') {
    return a.enrollment ? a.enrollment.sequence_name : 'In progress'
  }
  if (a.run_status === 'needs_attention') return 'Needs attention'
  if (a.run_status === 'not_eligible') return 'Not eligible'
  if (a.run_status === 'complete') return 'Complete'
  if (a.run_status === 'cancelled') return 'Cancelled'
  return humanize(a.run_status)
}

function actionStatusPillClass(a: MembershipAction): string {
  if (a.run_status === 'needs_attention') return 'pillDanger'
  if (a.run_status === 'not_eligible' || a.run_status === 'cancelled') return 'pillMuted'
  if (a.run_status === 'dispatched' || a.run_status === 'in_progress') return 'pillInfo'
  if (a.run_status === 'complete') return 'pillSuccess'
  return 'pillMuted'
}

function seqProgress(e: SeqEnrollment): number {
  if (!e.total_steps) return 0
  if (e.status === 'completed') return 100
  return Math.round(((e.current_step_index + 1) / e.total_steps) * 100)
}

type ResolvingState = {
  playId: string
  actionId: string
  actionRunId: string
  membershipId: string
  reason: string | null
  contactLabel: string
  playName: string
  actionType: string
}
const resolving = ref<ResolvingState | null>(null)

function openResolveDialog(m: PlayMembership, a: MembershipAction) {
  if (!a.action_run_id) return
  resolving.value = {
    playId: m.sales_play_id,
    actionId: a.action_id,
    actionRunId: a.action_run_id,
    membershipId: m.membership_id,
    reason: a.reason,
    contactLabel: primaryName.value || contactEmail.value || 'Contact',
    playName: m.sales_play_name,
    actionType: a.action_type,
  }
}

async function onResolved() {
  resolving.value = null
  await loadLifecycle()
}

function humanize(s: string): string {
  return s.replace(/[_-]+/g, ' ').replace(/\b(\w)/g, m => m.toUpperCase())
}

// ── CRM sync status ────────────────────────────────────────────
const crmRefreshing = ref(false)
const crmSyncedAt = ref<number | null>(null)
const tick = ref(0)
let tickTimer: ReturnType<typeof setInterval> | null = null

const crmSyncLabel = computed(() => {
  void tick.value
  const t = crmSyncedAt.value
  if (!t) return ''
  const secs = Math.round((Date.now() - t) / 1000)
  if (secs < 10) return 'Synced just now'
  if (secs < 60) return `Synced ${secs}s ago`
  const mins = Math.round(secs / 60)
  if (mins < 60) return `Synced ${mins}m ago`
  return `Synced ${Math.round(mins / 60)}h ago`
})

function refreshCrms() {
  const id = accountId.value
  if (!id) return
  crmRefreshing.value = true
  const opts = { method: 'POST' } as const
  Promise.allSettled([
    apiFetch(`/accounts/${id}/refresh_salesforce?force=false`, opts),
    apiFetch(`/accounts/${id}/refresh_pipedrive?force=false`, opts),
    apiFetch(`/accounts/${id}/refresh_hubspot?force=false`, opts),
  ]).then(() => {
    crmRefreshing.value = false
    crmSyncedAt.value = Date.now()
  })
}

// ── Data loading ────────────────────────────────────────────────
async function loadAll() {
  const cid = routeContactId.value
  if (!cid) {
    contact.value = null
    accountId.value = ''
    lifecycle.value = null
    return
  }

  // 1. Fetch contact + account_id from flat endpoint
  try {
    const resp = await apiFetch(`/contacts/${encodeURIComponent(cid)}/detail`)
    contact.value = resp.contact
    accountId.value = String(resp.account_id || '')
    loadError.value = false
  } catch {
    contact.value = null
    accountId.value = ''
    loadError.value = true
    return
  }

  // 2. Set drawer context now that we have both IDs
  setDrawerContext()

  // 3. Fire-and-forget CRM refresh to warm cache tables
  refreshCrms()

  // 4. Parallel loads that depend on the resolved accountId
  await Promise.all([
    loadLifecycle(),
  ])
}

async function loadLifecycle() {
  const cid = routeContactId.value
  if (!cid) { lifecycle.value = null; return }
  try { lifecycle.value = await apiFetch(`/contacts/${encodeURIComponent(cid)}/lifecycle-card`) }
  catch { lifecycle.value = null }
}

function setDrawerContext() {
  const aid = accountId.value
  const cid = routeContactId.value
  if (!aid || !cid) return
  setContext({ type: DRAWER_CONTEXT_CONTACT, contactId: cid, accountId: aid })
  openDrawer()
}

watch(routeContactId, () => { void loadAll() })

onMounted(async () => {
  void loadAll()
  tickTimer = setInterval(() => { tick.value++ }, 30_000)
  await preloadCrmLinkConfig()
  crmReady.value = true
})

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer)
})
</script>

<style scoped>
.contactDetail {
  display: flex;
  flex-direction: column;
  gap: var(--space-6, 24px);
}

/* ── Header ──────────────────────────────────────────────────────── */
.ctHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4, 16px);
}

.ctHeaderRight {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  flex-shrink: 0;
}

.ctIdentity {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4, 16px);
  min-width: 0;
}

.ctLogo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg, 12px);
  background: var(--color-bg-surface, #fff);
  box-shadow: var(--shadow-neu-out-cream, 0 2px 8px rgba(0,0,0,.08));
  flex-shrink: 0;
  overflow: hidden;
  margin-top: 2px;
}

.ctText {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.ctName {
  font-family: var(--font-sans);
  font-size: var(--fs-xl, 20px);
  font-weight: var(--fw-bold, 700);
  color: var(--color-text-primary, #1a1a1a);
  letter-spacing: var(--tracking-tight, -0.01em);
  margin: 0;
}

.ctSubline {
  font-size: var(--fs-sm, 14px);
  color: var(--color-text-secondary, #525252);
}

.ctMeta {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  flex-wrap: wrap;
  font-size: var(--fs-sm, 14px);
}

.ctCompany {
  color: var(--color-text-tertiary, #737373);
  text-decoration: none;
  transition: color 150ms ease;
}
.ctCompany:hover {
  color: var(--color-accent, #059669);
  text-decoration: underline;
}

.ctDetail {
  color: var(--color-text-tertiary, #737373);
}
.ctDetail::before {
  content: '·';
  margin-right: var(--space-3, 12px);
  color: var(--color-border, #d4d4d4);
}

.ctLinks {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  margin-top: 2px;
}

.ctExtLink {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-text-tertiary, #737373);
  text-decoration: none;
  transition: color 150ms ease;
}
.ctExtLink:hover { color: var(--color-accent, #059669); }

/* ── CRM sync indicator ─────────────────────────────────────────── */
.crmSync {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.crmSpinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--color-border, #e5e2dc);
  border-top-color: var(--color-accent, #059669);
  border-radius: 50%;
  display: inline-block;
  animation: crm-spin 0.8s linear infinite;
}

@keyframes crm-spin {
  to { transform: rotate(360deg); }
}

.crmSyncLabel {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
}

/* ── Sales play section ──────────────────────────────────────────── */
.sectionTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-md, 16px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-primary, #1a1a1a);
  margin: 0 0 var(--space-3, 12px);
}

.spCards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.spCard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #faf9f6);
  border: 1px solid var(--color-border, #e5e2dc);
}

.spCardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.spName {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a1a);
  text-decoration: none;
  transition: color 150ms ease;
}
.spName:hover { color: var(--color-accent, #059669); text-decoration: underline; }

.spStatus {
  font-size: 12px;
  color: var(--color-text-tertiary, #737373);
}

.spSeq {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  background: color-mix(in srgb, var(--color-bg-surface, #fff) 80%, var(--color-border, #ddd) 20%);
}

.spSeqHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.spSeqName {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a1a);
}

.spSeqProgress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progressBar {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: var(--color-border, #e5e2dc);
  overflow: hidden;
}

.progressFill {
  height: 100%;
  border-radius: 2px;
  background: var(--color-accent, #059669);
  transition: width 0.3s ease;
}

.progressLabel {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
}

.spSeqReason {
  font-size: 12px;
  color: var(--color-danger, #b91c1c);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.spSeqComment {
  font-size: 12px;
  color: var(--color-text-tertiary, #737373);
}

.resolveBtn {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-accent, #059669);
  background: none;
  border: 1px solid var(--color-border, #ddd);
  border-radius: 4px;
  padding: 1px 8px;
  cursor: pointer;
}

/* ── Pills ───────────────────────────────────────────────────────── */
.statePill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.4;
  white-space: nowrap;
}
.statePill.mini {
  font-size: 9px;
  padding: 1px 6px;
}
.pillSuccess { background: var(--color-success-soft, #ecfdf5); color: var(--color-success, #047857); }
.pillInfo { background: var(--color-info-soft, #eff6ff); color: var(--color-info, #1d4ed8); }
.pillWarning { background: var(--color-warning-soft, #fff7ed); color: var(--color-warning, #c2410c); }
.pillDanger { background: var(--color-danger-soft, #fef2f2); color: var(--color-danger, #b91c1c); }
.pillMuted { background: var(--color-muted-soft, #f5f5f5); color: var(--color-muted, #737373); }

.muted {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
}

.loadMsg {
  padding: var(--space-5, 20px);
}
</style>
